import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, ExternalLink, Receipt, Wallet } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import BackButton from '../../../../components/BackButton';
import { formatCurrencyAmount, getFormattedDate } from '../../../../utils/miscFunctions';
import Loader from '../../../../components/Loader';
import useLoans from '../../../../hooks/loansHook';
import { ITableAction } from '../../../../components/elements/GTable/GTable';
import Loan from '../../../../models/Loan';
import Transaction from '../../../../models/Transaction';
import { getTransactionById } from '../../../../services/transactions';
import useTransactions from '../../../../hooks/transactionsHook';
import clsx from 'clsx';


const TransactionDetailPage: React.FC = () => {
  const { transactionId } = useParams<{ transactionId: string }>();
  const navigate = useNavigate();
  const { TypeBadge, StatusBadge } = useTransactions({});
  const { handleRemoveLoanFromDisbursement, getRouteFromProductType } = useLoans({});
  const [transactionDetail, setTransactionDetail] = useState<Transaction | null>(null);

  const getLoanTableRowActions = (loan: Loan) => {
    const tableActions: ITableAction[] = [
      {
        label: 'View',
        callback: () => navigate(getRouteFromProductType(loan))
      },
    ];

    if (!(transactionDetail?.isCompleted() || transactionDetail?.isPartial())) {
      tableActions.push({
        label: 'Remove from disbursement',
        callback: () => handleRemoveLoanFromDisbursement(transactionDetail?.id, Number(loan?.id))
      });
    }
    return tableActions;
  }

  const { columns } = useLoans({ tableRowActions: getLoanTableRowActions, showColumCheckbox: false });

  const getTransactionDetail = async () => {
    if (!transactionId) return;
    try {
      const response: any = await getTransactionById(transactionId);
      if (response?.data) {
        setTransactionDetail(new Transaction(response?.data));
      }
    } catch (error) {
      console.log(error);
    }
  }

  useQuery({
    queryKey: ['getTransactionDetail', transactionId],
    queryFn: () => getTransactionDetail()
  });


  if (!transactionDetail) {
    return (
       <div className="h-screen flex items-center justify-center w-full">
          <Loader message="Loading transaction details..." brollyLoaderFixed  />
        </div>
    );
  }

  return (
    <div className="">
      {/* Back Button */}
      <div className="mb-6">
        <BackButton onClick={() => navigate(-1)} />
      </div>

      {/* Transaction Header */}
      <div className="bg-white dark:bg-darkBg p-6 rounded-2xl shadow mb-6 dark:!text-darkBg-mainText dark:border dark:border-darkBg-border">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center">
          <div>
            <div className="flex items-center mb-2">
              <h1 className="text-2xl font-bold mr-3 text-gray-900 dark:text-darkBg-mainText">Transaction #{transactionDetail.reference}</h1>
              <TypeBadge type={transactionDetail.type} />
            </div>
            <p className="text-gray-500 dark:text-darkBg-subText mb-2">
              {transactionDetail.isDisbursement() ? 'To' : 'From'} {transactionDetail.isDisbursement() ? transactionDetail.toCompanyName : transactionDetail.fromCompanyName}
            </p>
            <StatusBadge status={transactionDetail.status} />
          </div>

          <div className="mt-4 md:mt-0 text-right">
            <div className="text-gray-500 dark:text-darkBg-subText mb-1">Amount</div>
            <p className={`text-3xl font-bold ${transactionDetail.isLoanRepayment() ? 'text-green-600 dark:text-green-400' : 'text-indigo-600 dark:text-indigo-400'
              }`}>
              {transactionDetail.isLoanRepayment() ? '+' : '-'}{formatCurrencyAmount(transactionDetail.amount)}
            </p>
            <div className="flex items-center mt-2 justify-end">
              <Calendar className="w-4 h-4 mr-2 text-gray-500 dark:text-darkBg-subText" />
              <span className="dark:text-darkBg-mainText">{getFormattedDate({ date: transactionDetail.transactionDate})}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Transaction Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Left Column - Basic Details */}
        <TransactionDetailsItemCard title='Transaction Details' classes='grid grid-cols-1' icon={<Receipt className='h-6 w-6 mr-2 text-gray-400'/>}>

          
        <div className="">
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-darkBg-subText">Reference:</span>
              <span className="font-medium dark:text-darkBg-mainText">{transactionDetail.reference}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-darkBg-subText">Type:</span>
              <TypeBadge type={transactionDetail.type} />
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-darkBg-subText">Status:</span>
              <StatusBadge status={transactionDetail.status} />
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-darkBg-subText">Amount:</span>
              <span className="font-medium dark:text-darkBg-mainText">{formatCurrencyAmount(transactionDetail.amount)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-darkBg-subText">Processing Fee:</span>
              <span className="font-medium dark:text-darkBg-mainText">{formatCurrencyAmount(transactionDetail.amount)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-darkBg-subText">Net Amount:</span>
              <span className="font-medium dark:text-darkBg-mainText">{formatCurrencyAmount(transactionDetail.amount)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-darkBg-subText">Payment Method:</span>
              <span className="text-gray-600">-</span>
              {/* <span className="font-medium capitalize">{transactionDetail.paymentMethod.replace('_', ' ')}</span> */}
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-darkBg-subText">Date:</span>
              <span className="font-medium dark:text-darkBg-mainText">
                {transactionDetail.transactionDate ? getFormattedDate({ date: transactionDetail.transactionDate }) : '-'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-darkBg-subText">Processed By:</span>
               <span className="font-medium dark:text-darkBg-mainText">-</span>
            </div>
          </div>
        </div>

        </TransactionDetailsItemCard>

        {/* Right Column - Payment Details */}
        <TransactionDetailsItemCard title='Payment Details' classes='grid grid-cols-1' icon={<Wallet className='h-6 w-6 mr-2 text-gray-400'/>}>
        <div className="">

          <div>
            <h3 className="font-semibold text-sm text-gray-500 dark:text-darkBg-subText mb-2">FROM</h3>
            <div className="p-3 border border-gray-200 dark:border-darkBg-border rounded-md mb-4">
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-darkBg-subText">Name:</span>
                  <span className="font-medium dark:text-darkBg-mainText">
                    {transactionDetail.fromCompanyName ? transactionDetail.fromCompanyName: '-'}
                  </span>
                </div>
              </div>
            </div>

            <h3 className="font-semibold text-sm text-gray-500 dark:text-darkBg-subText mb-2">TO</h3>
             <div className="p-3 border border-gray-200 dark:border-darkBg-border rounded-md mb-4">
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-darkBg-subText">Name:</span>
                  <span className="font-medium dark:text-darkBg-mainText">
                    {transactionDetail.toCompanyName ? transactionDetail.toCompanyName : '-'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="font-semibold text-gray-500 dark:text-darkBg-subText mb-3">Additional Information</h3>
            <div className="space-y-2">
              <div className="">
                <span className="text-gray-600 dark:text-darkBg-subText">Description:</span>
                <p className="font-medium text-[13px]   dark:text-darkBg-mainText">
                  {transactionDetail.description}
                </p>
              </div>
              {/* {Object.entries(transactionDetail.metadata).map(([key, value], index) => (
                <div key={index} className="flex justify-between">
                  <span className="text-gray-600 dark:text-darkBg-subText">{key}:</span>
                  <span className="font-medium text-right">{value}</span>
                </div>
              ))} */}
            </div>
          </div>
        </div>
        </TransactionDetailsItemCard>
      </div>

      {/* Timeline */}


      {/* Action Buttons */}
      <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-darkBg border-t border-gray-200 dark:border-darkBg-border p-4 flex justify-end space-x-4">
        {transactionDetail.status === 'pending' && (
          <>
            <button className="px-4 py-2 bg-green-600 dark:bg-green-700 text-white rounded-md hover:bg-green-700 dark:hover:bg-green-800">
              Approve
            </button>
            <button className="px-4 py-2 bg-red-600 dark:bg-red-700 text-white rounded-md hover:bg-red-700 dark:hover:bg-red-800">
              Reject
            </button>
          </>
        )}
        {transactionDetail.status === 'completed' && (
          <button className="px-4 py-2 bg-indigo-600 dark:bg-indigo-700 text-white rounded-md hover:bg-indigo-700 dark:hover:bg-indigo-800 flex items-center">
            <Receipt className="h-4 w-4 mr-2" />
            Download Receipt
          </button>
        )}
        {transactionDetail.status === 'failed' && (
          <button className="px-4 py-2 bg-indigo-600 dark:bg-indigo-700 text-white rounded-md hover:bg-indigo-700 dark:hover:bg-indigo-800">
            Retry Transaction
          </button>
        )}
        <button className="px-4 py-2 bg-white dark:bg-darkBg border border-gray-300 dark:border-darkBg-border text-gray-800 dark:text-darkBg-mainText rounded-md hover:bg-gray-50 dark:hover:bg-darkBg-border flex items-center">
          <ExternalLink className="h-4 w-4 mr-2" />
          View in Banking Portal
        </button>
      </div>
    </div>
  );
};

export default TransactionDetailPage;

interface ITransactionDetailsItemCard {
  title: string;
  classes?: string;
  children: JSX.Element;
  icon?: React.ReactElement;
}
const TransactionDetailsItemCard = ({
  title,
  classes,
  children,
  icon
}: ITransactionDetailsItemCard): JSX.Element => {
  return (
    <div className="mt-5 bg-white dark:bg-darkBg rounded-2xl shadow-md overflow-hidden dark:!text-darkBg-mainText dark:border dark:border-darkBg-border">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-darkBg-border bg-gray-50 dark:bg-darkBg">
        <h2 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText flex items-center">
          {icon}
          {title}
        </h2>
      </div>
      <div
        className={clsx(
          'grid py-6  px-4 rounded-2xl h-full grid-cols-1 gap-5 ',
          classes ? classes : 'md:grid-cols-2 '
        )}>
        {children}
      </div>
    </div>
  );
};